/**
 * HeroSection - Full-viewport narrative hero (Narrative Shell).
 *
 * A 100vh dark-gradient landing section that opens the narrative shell.
 * Contains a centered headline + subtitle, ~22 slowly falling white
 * particles (CSS @keyframes), and a bouncing downward chevron at the
 * bottom inviting the user to scroll.
 *
 * 全屏叙事 Hero 区：100vh 暗色渐变背景，居中主标题与副标题，约 22 颗白色
 * 粒子缓慢下落（CSS @keyframes），底部一个跳动向下箭头引导用户继续滚动。
 */
import type { CSSProperties } from 'react';
import HeroBackground3D from './HeroBackground3D';

// ----- Animation keyframes (injected via <style>) -----
// 动画关键帧（通过 <style> 注入）

const HERO_CSS = `
@keyframes narrativeParticleFall {
  0%   { transform: translateY(-10px); opacity: 0; }
  10%  { opacity: 0.7; }
  90%  { opacity: 0.7; }
  100% { transform: translateY(100vh); opacity: 0; }
}
@keyframes narrativeArrowBounce {
  0%, 100% { transform: translateY(0) rotate(-45deg); }
  50%      { transform: translateY(8px) rotate(-45deg); }
}
`;

// ----- Particles -----
// 20-25 white circles, random left / delay / 8-15s duration, linear infinite.
// 20-25 颗白色圆点，随机 left / delay / 8-15s 时长，线性无限循环。
const PARTICLES = Array.from({ length: 22 }, () => ({
  left: Math.random() * 100, // 0-100 %
  delay: Math.random() * 7, // 0-7 s
  duration: 8 + Math.random() * 7, // 8-15 s
  size: 2 + Math.floor(Math.random() * 2), // 2 or 3 px
}));

// ----- Styles -----

const heroStyle: CSSProperties = {
  position: 'relative',
  height: '100vh',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'var(--space-xl)',
  background:
    'linear-gradient(180deg, var(--bg-base) 0%, var(--bg-elevated) 100%)',
  overflow: 'hidden',
};

const centerStyle: CSSProperties = {
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 'var(--space-md)',
  textAlign: 'center',
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--fs-4xl)',
  fontFamily: 'var(--font-mono)',
  color: 'var(--text-primary)',
  fontWeight: 'var(--fw-bold)',
  textAlign: 'center',
  lineHeight: 1.2,
};

const subtitleStyle: CSSProperties = {
  margin: 0,
  fontSize: 'var(--fs-lg)',
  color: 'var(--text-secondary)',
  textAlign: 'center',
};

const arrowWrapStyle: CSSProperties = {
  position: 'absolute',
  bottom: 'var(--space-xl)',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 1,
};

// border-left + border-bottom rotated -45deg forms a downward chevron.
// border-left + border-bottom 旋转 -45deg 即构成向下箭头。
const arrowStyle: CSSProperties = {
  width: 22,
  height: 22,
  borderLeft: '2px solid var(--text-secondary)',
  borderBottom: '2px solid var(--text-secondary)',
  animation: 'narrativeArrowBounce 2s var(--ease-in-out) infinite',
};

// ----- Component -----

export default function HeroSection() {
  return (
    <section style={heroStyle}>
      <style>{HERO_CSS}</style>

      {/* 3D line-surface background (透明叠加在渐变之上;zIndex:0) / 3D 线条曲面背景 */}
      <HeroBackground3D />

      {/* Falling particles (subtle, layered above the 3D surface) / 下落粒子 */}
      {PARTICLES.map((p, i) => (
        <span
          key={i}
          style={{
            position: 'absolute',
            top: 0,
            left: `${p.left}%`,
            width: p.size,
            height: p.size,
            borderRadius: '50%',
            background: '#FFFFFF',
            opacity: 0.5,
            animation: `narrativeParticleFall ${p.duration}s linear ${p.delay}s infinite`,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      ))}

      {/* Centered headline + subtitle / 居中主标题与副标题 */}
      <div style={centerStyle}>
        <h1 style={titleStyle}>让脚下的每一次发力都被看见</h1>
        <p style={subtitleStyle}>滑雪智能鞋垫，逐弯复盘教练</p>
      </div>

      {/* Bouncing down-chevron / 跳动向下箭头 */}
      <div style={arrowWrapStyle}>
        <div style={arrowStyle} />
      </div>
    </section>
  );
}
